import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export function downloadFatigueRulesListPdf() {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const left = 48;
  const right = pageWidth - 48;
  const maroon = [138, 24, 49];
  const topMargin = 56;
  const bottomMargin = pageHeight - 48;
  const getSectionStartY = (candidateY, minSpaceNeeded = 100) => {
    if (candidateY + minSpaceNeeded > bottomMargin) {
      doc.addPage();
      return topMargin;
    }
    return candidateY;
  };

  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.setTextColor(...maroon);
  doc.text("Fatigue Risk Management - Rules List", pageWidth / 2, 48, { align: "center" });

  doc.setDrawColor(...maroon);
  doc.setLineWidth(1);
  doc.line(left, 58, right, 58);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text("A. Basic Working Rules", left, 82);

  let y = 98;
  const drawRule = (titleText, desc, extra) => {
    y = getSectionStartY(y, 50);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9.5);
    doc.setTextColor(20, 20, 20);
    const titleLines = doc.splitTextToSize(titleText, right - left);
    doc.text(titleLines, left, y);
    y += titleLines.length * 12 + 2;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.4);
    doc.setTextColor(50, 50, 50);
    const descLines = doc.splitTextToSize(desc, right - left);
    doc.text(descLines, left, y);
    y += descLines.length * 11;
    if (extra) {
      const extraLines = doc.splitTextToSize(extra, right - left);
      doc.text(extraLines, left, y);
      y += extraLines.length * 11;
    }
    y += 6;
  };

  drawRule(
    "Rule 1: An employee must have at least 12 hours rest between shifts.",
    "This is measured from the time the employee left the last place of work (outbound shift) to the time they started at the first place of work (oncoming shift)."
  );
  drawRule(
    "Rule 2: A single shift must not be longer than 12 hours.",
    "The shift duration is the time from on-duty (arrival at first place of work) to off-duty (departure from last place of work). Break time is not deducted from the shift duration for the purpose of this rule."
  );
  drawRule(
    "Rule 3: An employee must not work more than 72 hours in any rolling 7 days.",
    "Rolling 7 days means any consecutive 7-day window ending on the current day. The total is the sum of all shift durations within that window. Rest days contribute 0 hours but do not reset the rolling total."
  );
  drawRule(
    "Rule 4: An employee must not work more than 13 shifts in any rolling 14 days.",
    "Rolling 14 days means any consecutive 14-day window ending on the current day. Each day with a recorded shift counts as 1. Rest days are not counted. 13 shifts in 14 days is allowed. The 14th shift in a 14-day window triggers a breach."
  );
  drawRule(
    "Rule 5: Total door-to-door time must not exceed 14 hours.",
    "Door-to-door time = travel time from place of rest to first place of work + shift duration + travel time from last place of work to place of rest. In the app this is calculated as: shift duration + (one-way commute time x 2)."
  );

  autoTable(doc, {
    startY: y + 4,
    margin: { left, right: left },
    head: [["Rule", "Condition", "Limit"]],
    body: [
      ["1", "Rest between shifts", "Minimum 12 hours"],
      ["2", "Single shift duration", "Maximum 12 hours"],
      ["3", "Hours in rolling 7 days", "Maximum 72 hours"],
      ["4", "Shifts in rolling 14 days", "Maximum 13 shifts"],
      ["5", "Door-to-door time", "Maximum 14 hours"],
    ],
    styles: {
      font: "helvetica",
      fontSize: 8.5,
      textColor: [20, 20, 20],
      lineColor: [210, 210, 210],
      lineWidth: 0.5,
      cellPadding: { top: 4, right: 5, bottom: 4, left: 5 },
    },
    headStyles: { fillColor: maroon, textColor: [255, 255, 255], fontStyle: "bold" },
    columnStyles: {
      0: { halign: "center", cellWidth: 44, fontStyle: "bold" },
      1: { cellWidth: 300 },
      2: { cellWidth: 130, halign: "center", fontStyle: "bold" },
    },
  });

  let y2 = getSectionStartY((doc.lastAutoTable?.finalY || y) + 24, 200);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(...maroon);
  doc.text("B. Fatigue Score Rules (FRI)", left, y2);
  y2 += 18;

  const drawBRule = (titleText, desc) => {
    y2 = getSectionStartY(y2, 44);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9.2);
    doc.setTextColor(20, 20, 20);
    const tl = doc.splitTextToSize(titleText, right - left);
    doc.text(tl, left, y2);
    y2 += tl.length * 11 + 2;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.2);
    doc.setTextColor(55, 55, 55);
    const dl = doc.splitTextToSize(desc, right - left);
    doc.text(dl, left, y2);
    y2 += dl.length * 10.5 + 8;
  };

  drawBRule(
    "Rule 6: If day shift fatigue score is 35 or more, it is a fatigue issue.",
    "The Fatigue Index (FI) is calculated using the HSE fatigue model. This threshold applies only to day shifts. A day shift is one that starts between 06:00 and 17:59 and does not cross midnight."
  );
  drawBRule(
    "Rule 7: If night shift fatigue score is 45 or more, it is a fatigue issue.",
    "A higher threshold is used for night shifts because the fatigue model already produces higher scores during night hours due to the circadian component. A night shift is one that starts at or after 18:00, or before 06:00, or crosses midnight."
  );
  drawBRule(
    "Rule 8: If fatigue risk score is 1.6 or more, it is a fatigue issue. This applies to any shift.",
    "The Risk Index (RI) is a multiplier calculated by the HSE fatigue model. A value of 1.0 represents baseline risk. Values above 1.0 indicate elevated risk. This rule applies to both day and night shifts."
  );

  autoTable(doc, {
    startY: y2 + 4,
    margin: { left, right: left },
    head: [["Rule", "Condition", "Threshold"]],
    body: [
      ["6", "Day shift Fatigue Index (FI)", "FI >= 35"],
      ["7", "Night shift Fatigue Index (FI)", "FI >= 45"],
      ["8", "Risk Index (RI) - any shift", "RI >= 1.6"],
    ],
    styles: {
      font: "helvetica",
      fontSize: 8.5,
      textColor: [20, 20, 20],
      lineColor: [210, 210, 210],
      lineWidth: 0.5,
      cellPadding: { top: 4, right: 5, bottom: 4, left: 5 },
    },
    headStyles: { fillColor: maroon, textColor: [255, 255, 255], fontStyle: "bold" },
    columnStyles: {
      0: { halign: "center", cellWidth: 44, fontStyle: "bold" },
      1: { cellWidth: 300 },
      2: { cellWidth: 130, halign: "center", fontStyle: "bold" },
    },
  });

  let y3 = getSectionStartY((doc.lastAutoTable?.finalY || y2) + 24, 280);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(...maroon);
  doc.text("C. Exceedance Levels", left, y3);
  y3 += 18;

  const drawCRule = (titleText, desc) => {
    y3 = getSectionStartY(y3, 36);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(20, 20, 20);
    const tl = doc.splitTextToSize(titleText, right - left);
    doc.text(tl, left, y3);
    y3 += tl.length * 11 + 2;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(58, 58, 58);
    if (desc.includes("\n")) {
      desc.split("\n").forEach((line) => {
        const wrapped = doc.splitTextToSize(line, right - left);
        doc.text(wrapped, left, y3);
        y3 += wrapped.length * 10.5 + 2;
      });
      y3 += 6;
    } else {
      const lines = doc.splitTextToSize(desc, right - left);
      doc.text(lines, left, y3);
      y3 += lines.length * 10.5 + 8;
    }
  };

  drawCRule(
    "Rule 9: General exceedance happens when fatigue scores are high (Rules 6, 7, 8) plus any of the following:",
    "- Working more than 12 hours in one shift\n- Exceeding more than 14 hours door-to-door\n- Less than 12 hours break between booking off from one shift and starting the next\n- Working more than 13 consecutive turns of duty in 14 rolling days"
  );
  drawCRule("Rule 10: A fatigue assessment must be done.", "Form: SF-008-A General Exceedance of Working Hours Assessment.");
  drawCRule(
    "Rule 11: Level 1 exceedance happens when more than 60 hours are worked in 7 rolling days.",
    "This applies when hours are 60 or more but less than 72 hours."
  );
  drawCRule(
    "Rule 12: Extra controls are required for Level 1.",
    "No driving duties. Must not work alone. Additional supervision required. Form: SF-008-B Level 1 Exceedance Fatigue Risk Assessment."
  );
  drawCRule(
    "Rule 13: Level 2 exceedance (serious) happens when any of the following occur:",
    "- More than 72 hours in 7 rolling days\n- Less than 12 hours rest between shifts\n- Shift longer than 12 hours\n- Door-to-door time is 14 hours or more\n- More than 13 shifts in 14 rolling days"
  );
  drawCRule(
    "Rule 14: Strong restrictions apply for Level 2 and 24 hours rest is required.",
    "The employee must be removed from duty. A minimum of 24 hours rest is required before the next shift. Form: SF-008-C Level 2 Exceedance of Working Hours Assessment."
  );

  autoTable(doc, {
    startY: y3 + 4,
    margin: { left, right: left },
    head: [["Level", "Trigger", "Form"]],
    body: [
      ["General", "Any of Rules 1, 2, 4, 5 breached, or FI/RI thresholds exceeded (Rules 6, 7, 8)", "SF-008-A"],
      ["Level 1", "60 to 72 hours worked in rolling 7 days (Rule 3)", "SF-008-B"],
      ["Level 2", "More than 72h in 7 days, or rest < 12h, or shift > 12h, or door-to-door >= 14h, or > 13 shifts in 14 days", "SF-008-C"],
    ],
    styles: {
      font: "helvetica",
      fontSize: 7.8,
      textColor: [20, 20, 20],
      lineColor: [208, 208, 208],
      lineWidth: 0.5,
      cellPadding: { top: 3, right: 5, bottom: 3, left: 5 },
    },
    headStyles: { fillColor: maroon, textColor: [255, 255, 255], fontStyle: "bold" },
    columnStyles: { 0: { cellWidth: 52, fontStyle: "bold" }, 1: { cellWidth: 350 }, 2: { cellWidth: 72, halign: "center", fontStyle: "bold" } },
    didParseCell: (data) => {
      if (data.section === "body" && data.column.index === 0) {
        if (data.cell.raw === "General") data.cell.styles.fillColor = [235, 245, 235];
        if (data.cell.raw === "Level 1") data.cell.styles.fillColor = [255, 248, 220];
        if (data.cell.raw === "Level 2") data.cell.styles.fillColor = [255, 239, 239];
      }
    },
  });

  let yFoot = (doc.lastAutoTable?.finalY || y3) + 10;
  yFoot = getSectionStartY(yFoot, 16);
  doc.setFont("helvetica", "italic");
  doc.setFontSize(7.5);
  doc.setTextColor(90, 90, 90);
  doc.text("If a Level 1 or Level 2 is triggered along with General triggers, the Level 1 or Level 2 takes priority over the General.", left, yFoot);

  let y4 = getSectionStartY(yFoot + 18, 120);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(...maroon);
  doc.text("D. Forms and Actions", left, y4);
  y4 += 18;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(20, 20, 20);
  doc.text("Rule 15: Every exceedance creates a form automatically.", left, y4);
  y4 += 14;
  doc.text("Rule 16: The employee must complete the form.", left, y4);
  y4 += 14;
  doc.text("Rule 17: A manager must review and approve the actions.", left, y4);
  y4 += 16;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(58, 58, 58);
  const mgrLine = doc.splitTextToSize("The manager may require more risk mitigation which the Line Manager puts into place.", right - left);
  doc.text(mgrLine, left, y4);
  y4 += mgrLine.length * 10.5 + 10;

  y4 = getSectionStartY(y4, 80);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(20, 20, 20);
  doc.text("Rule 18: Serious cases require a fatigue management plan.", left, y4);
  y4 += 12;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  const r18a = doc.splitTextToSize(
    "Any fatigue risk assessment form can identify that a SF-008-D Fatigue Management Plan is required. The plan is not mandatory for every exceedance. It is created by the manager when the fatigue trigger cannot be removed by changing shift times or travel arrangements.",
    right - left
  );
  doc.text(r18a, left, y4);
  y4 += r18a.length * 10.5 + 8;
  const r18b = doc.splitTextToSize(
    "Nobody can override fatigue rules. All triggers must either be worked through to eliminate (change shift times, travel times, etc. which sees the fatigue trigger removed). If the trigger cannot be removed then the appropriate risk assessment documentation needs to be completed.",
    right - left
  );
  doc.text(r18b, left, y4);
  y4 += r18b.length * 10.5 + 14;

  y4 = getSectionStartY(y4, 40);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(...maroon);
  doc.text("E. Driving Rules", left, y4);
  y4 += 16;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(20, 20, 20);
  doc.text("Rule 19: Driving should normally be no more than 1 hour each way.", left, y4);
  y4 += 20;
  let y5 = getSectionStartY(y4, 320);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.setTextColor(...maroon);
  doc.text("Exceedance Mapping - Which Rule Triggers Which Level", left, y5);

  autoTable(doc, {
    startY: y5 + 10,
    margin: { left, right: left },
    head: [["Condition", "Rule", "General", "Level 1", "Level 2"]],
    body: [
      ["Rest < 12 hours between shifts", "1", "Yes", "-", "Yes"],
      ["Shift longer than 12 hours", "2", "Yes", "-", "Yes"],
      ["60 to 72 hours in rolling 7 days", "3", "-", "Yes", "-"],
      ["More than 72 hours in rolling 7 days", "3", "-", "-", "Yes"],
      ["More than 13 shifts in rolling 14 days", "4", "Yes", "-", "Yes"],
      ["Door-to-door time 14 hours or more", "5", "Yes", "-", "Yes"],
      ["Day shift FI score >= 35", "6", "Yes", "-", "-"],
      ["Night shift FI score >= 45", "7", "Yes", "-", "-"],
      ["Risk Index >= 1.6 (any shift)", "8", "Yes", "-", "-"],
    ],
    styles: {
      font: "helvetica",
      fontSize: 7.6,
      textColor: [20, 20, 20],
      lineColor: [208, 208, 208],
      lineWidth: 0.5,
      cellPadding: { top: 3, right: 4, bottom: 3, left: 4 },
    },
    headStyles: { fillColor: maroon, textColor: [255, 255, 255], fontStyle: "bold" },
    columnStyles: {
      0: { cellWidth: 168 },
      1: { cellWidth: 34, halign: "center", fontStyle: "bold" },
      2: { cellWidth: 52, halign: "center" },
      3: { cellWidth: 52, halign: "center" },
      4: { cellWidth: 52, halign: "center" },
    },
  });

  let y6 = (doc.lastAutoTable?.finalY || y5) + 20;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.setTextColor(...maroon);
  doc.text("Forms Reference", left, y6);

  autoTable(doc, {
    startY: y6 + 8,
    margin: { left, right: left },
    head: [["Form", "Name", "Trigger", "Filled by"]],
    body: [
      ["SF-008-A", "General Exceedance of Working Hours Assessment", "Any General exceedance", "Employee completes, Manager reviews and approves"],
      ["SF-008-B", "Level 1 Exceedance Fatigue Risk Assessment", "60 to 72 hours in rolling 7 days", "Employee completes, Manager reviews and approves"],
      ["SF-008-C", "Level 2 Exceedance of Working Hours Assessment", "Any Level 2 trigger", "Employee completes, Manager reviews and approves"],
      ["SF-008-D", "Fatigue Management Plan", "Created by manager when trigger cannot be removed", "Manager creates, Manager and Employee complete together"],
    ],
    styles: {
      font: "helvetica",
      fontSize: 7.4,
      textColor: [20, 20, 20],
      lineColor: [208, 208, 208],
      lineWidth: 0.5,
      cellPadding: { top: 3, right: 4, bottom: 3, left: 4 },
    },
    headStyles: { fillColor: maroon, textColor: [255, 255, 255], fontStyle: "bold" },
    columnStyles: { 0: { cellWidth: 48, fontStyle: "bold" }, 1: { cellWidth: 118 }, 2: { cellWidth: 118 }, 3: { cellWidth: 120 } },
  });

  let y7 = (doc.lastAutoTable?.finalY || y6) + 20;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.setTextColor(...maroon);
  doc.text("Complete Rules List", left, y7);

  autoTable(doc, {
    startY: y7 + 8,
    margin: { left, right: left },
    head: [["#", "Section", "Rule"]],
    body: [
      ["1", "A", "An employee must have at least 12 hours rest between shifts."],
      ["2", "A", "A single shift must not be longer than 12 hours."],
      ["3", "A", "An employee must not work more than 72 hours in any rolling 7 days."],
      ["4", "A", "An employee must not work more than 13 shifts in 14 rolling days."],
      ["5", "A", "Total door-to-door time must not exceed 14 hours."],
    ],
    styles: {
      font: "helvetica",
      fontSize: 7.6,
      textColor: [20, 20, 20],
      lineColor: [208, 208, 208],
      lineWidth: 0.5,
      cellPadding: { top: 3, right: 4, bottom: 3, left: 4 },
    },
    headStyles: { fillColor: maroon, textColor: [255, 255, 255], fontStyle: "bold" },
    columnStyles: { 0: { cellWidth: 26, halign: "center", fontStyle: "bold" }, 1: { cellWidth: 36, halign: "center", fontStyle: "bold" }, 2: { cellWidth: 342 } },
  });

  const y8 = getSectionStartY((doc.lastAutoTable?.finalY || y7) + 8, 200);
  autoTable(doc, {
    startY: y8,
    margin: { left, right: left },
    body: [
      ["6", "B", "If day shift fatigue score >= 35, it is a fatigue issue."],
      ["7", "B", "If night shift fatigue score >= 45, it is a fatigue issue."],
      ["8", "B", "If fatigue risk score >= 1.6, it is a fatigue issue (any shift)."],
      ["9", "C", "General exceedance happens when fatigue scores are high or basic working rules are breached."],
      ["10", "C", "A fatigue assessment (SF-008-A) must be done for any general exceedance."],
      ["11", "C", "Level 1 exceedance happens when 60 or more hours (but less than 72) are worked in rolling 7 days."],
      ["12", "C", "Extra controls are required for Level 1: no driving, no working alone, additional supervision. Form: SF-008-B."],
      ["13", "C", "Level 2 exceedance happens when: >72h in 7 days, or rest <12h, or shift >12h, or door-to-door >=14h, or >13 shifts in 14 days."],
      ["14", "C", "Level 2 requires removal from duty and 24 hours mandatory rest before next shift. Form: SF-008-C."],
      ["15", "D", "Every exceedance creates a form automatically."],
      ["16", "D", "The employee must complete the form."],
      ["17", "D", "A manager must review and approve the actions."],
      ["18", "D", "Serious or recurring cases require a fatigue management plan (SF-008-D)."],
      ["19", "E", "Driving should normally be no more than 1 hour each way."],
    ],
    styles: {
      font: "helvetica",
      fontSize: 7.4,
      textColor: [20, 20, 20],
      lineColor: [208, 208, 208],
      lineWidth: 0.5,
      cellPadding: { top: 3, right: 4, bottom: 3, left: 4 },
    },
    columnStyles: { 0: { cellWidth: 26, halign: "center", fontStyle: "bold" }, 1: { cellWidth: 36, halign: "center", fontStyle: "bold" }, 2: { cellWidth: 342 } },
  });

  doc.save("Fatigue_Rules_List.pdf");
}
